import { useState, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  ButtonGroup,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import BoltIcon from "@mui/icons-material/Bolt";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { PageWithTitle } from "src/Common/components";
import { PairTable } from "./components/PairTable";
import { ManualPairDialog } from "./components/ManualPairDialog";
import { MatchingHistoryTab } from "./components/MatchingHistoryTab";
import {
  usePairs,
  usePairStats,
  useConfirmPair,
  useRejectPair,
  useRunMatching,
  useFetchMarkets,
  useMatcherSchedule,
  useToggleMatcherSchedule,
} from "./api/usePairMapper";

const MATCH_TYPES = [
  { value: "", label: "All" },
  { value: "manual", label: "Manual" },
  { value: "deterministic", label: "Deterministic" },
  { value: "ai_confirmed", label: "AI Confirmed" },
  { value: "keyword_fallback", label: "Keyword Fallback" },
  { value: "ai_pending", label: "AI Pending (legacy)" },
  { value: "keyword_pending", label: "KW Pending (legacy)" },
];

const STATUSES = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
];

export const PredictionMapperPage = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [matchedBy, setMatchedBy] = useState("");
  const [search, setSearch] = useState("");
  const [manualPairOpen, setManualPairOpen] = useState(false);
  const [tab, setTab] = useState(0);

  const { data, isLoading } = usePairs({
    status: status || undefined,
    matchedBy: matchedBy || undefined,
    search: search || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const { data: stats } = usePairStats();
  const confirmMutation = useConfirmPair();
  const rejectMutation = useRejectPair();
  const matchMutation = useRunMatching();
  const fetchMutation = useFetchMarkets();
  const scheduleQuery = useMatcherSchedule();
  const toggleSchedule = useToggleMatcherSchedule();

  const isMatchRunning = matchMutation.isLoading;
  const scheduleEnabled = scheduleQuery.data?.enabled ?? false;
  const scheduleLoading = scheduleQuery.isLoading || toggleSchedule.isLoading;

  const handleConfirm = useCallback(
    (id: string) => confirmMutation.mutate(id),
    [confirmMutation],
  );

  const handleReject = useCallback(
    (id: string) => {
      if (confirm("Reject this pair? Markets will be unlinked and cannot be re-matched.")) {
        rejectMutation.mutate(id);
      }
    },
    [rejectMutation],
  );

  return (
    <PageWithTitle>
      <Box px={2} py={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: "white" }}>Prediction Pair Mapper</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                scheduleEnabled
                  ? `Auto-run ON — polymarket fires at :00, kalshi at :30. Toggle OFF disables both EventBridge schedules AND drops reserved concurrency to 0 (drains in-flight workers via their own 600s timeout, no new fan-out).`
                  : `Auto-run OFF — both schedules DISABLED, reserved concurrency 0. Toggle ON re-arms schedules + restores concurrency to 6. Manual RUN ALL / FULL RUN buttons still work regardless.`
              }
            >
              <FormControlLabel
                sx={{ color: "white", mr: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={scheduleEnabled}
                    onChange={(e) => toggleSchedule.mutate(e.target.checked)}
                    disabled={scheduleLoading}
                    color="success"
                  />
                }
                label={
                  scheduleLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    `Auto-run ${scheduleEnabled ? "ON" : "OFF"}`
                  )
                }
              />
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddLinkIcon />}
              onClick={() => setManualPairOpen(true)}
            >
              Manual Pair
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                fetchMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={() => fetchMutation.mutate()}
              disabled={fetchMutation.isLoading || isMatchRunning}
            >
              Fetch Markets
            </Button>

            <ButtonGroup variant="contained" size="small" disabled={isMatchRunning}>
              <Tooltip title="Tier 1 only: deterministic matching (free, no AI)">
                <Button
                  startIcon={isMatchRunning ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
                  onClick={() => matchMutation.mutate({ tiers: "deterministic" })}
                >
                  Deterministic
                </Button>
              </Tooltip>
              <Tooltip title="Tier 1 + Tier 2: deterministic + AI confirmation">
                <Button
                  startIcon={<SmartToyIcon />}
                  onClick={() => matchMutation.mutate({ tiers: "deterministic,ai_confirmed" })}
                >
                  + AI
                </Button>
              </Tooltip>
              <Tooltip title="Tier 3 only: keyword matching with AI">
                <Button
                  startIcon={<ManageSearchIcon />}
                  onClick={() => matchMutation.mutate({ tiers: "keyword_fallback" })}
                >
                  Keywords
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Button
              variant="contained"
              size="small"
              startIcon={isMatchRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => matchMutation.mutate({})}
              disabled={isMatchRunning}
            >
              Run All
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={isMatchRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => {
                if (confirm("Full run: fetch all markets, match all unpaired with all tiers. Continue?")) {
                  matchMutation.mutate({ fullRun: true });
                }
              }}
              disabled={isMatchRunning}
            >
              Full Run
            </Button>
          </Stack>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}
        >
          <Tab label="Pairs" sx={{ textTransform: "none" }} />
          <Tab label="Matching History" sx={{ textTransform: "none" }} />
        </Tabs>

        {tab === 0 && (
          <>
            {stats && (
              <Stack direction="row" spacing={1} mb={2}>
                <Chip label={`Total: ${stats.total}`} size="small" />
                <Chip label={`Active: ${stats.active}`} size="small" color="success" />
                {(stats as any).deterministic > 0 && (
                  <Chip label={`Deterministic: ${(stats as any).deterministic}`} size="small" color="info" />
                )}
                {(stats as any).aiConfirmed > 0 && (
                  <Chip label={`AI Confirmed: ${(stats as any).aiConfirmed}`} size="small" color="primary" />
                )}
                {stats.pending > 0 && (
                  <Chip label={`AI Pending: ${stats.pending}`} size="small" color="warning" />
                )}
                {stats.keywordPending > 0 && (
                  <Chip label={`KW Pending: ${stats.keywordPending}`} size="small" color="warning" />
                )}
                <Chip label={`Expired: ${stats.expired}`} size="small" color="error" />
              </Stack>
            )}

            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Status"
                select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 140 }}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Match Type"
                select
                value={matchedBy}
                onChange={(e) => {
                  setMatchedBy(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 180 }}
              >
                {MATCH_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <PairTable
              pairs={data?.pairs || []}
              total={data?.total || 0}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onReject={handleReject}
            />
          </>
        )}

        {tab === 1 && <MatchingHistoryTab />}
      </Box>
      <ManualPairDialog
        open={manualPairOpen}
        onClose={() => setManualPairOpen(false)}
      />
    </PageWithTitle>
  );
};
